import React, {
  forwardRef,
  memo,
  MutableRefObject,
  ReactElement,
  Ref,
  RefAttributes,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import styles from '../../styles/partials/_tabs.css';
import Tab, { ITabProps } from './Tab';
import TabContent from './TabContent';

import ConnectedFormContext from '../Context/ConnectedFormContext';
import { ContextualMenuItemType, IconButton, IContextualMenuItem } from '@fluentui/react';
import { Maybe } from '../../graphql/schema/p4types';
import UserContext from '../Context/UserContext';
import { TabOrganizationModal } from './TabOrganizationModal';
import { checkPermission } from '../../utils/OperationPermissions';
import { SubmitOption } from '../../@types/connectedFormTypes';
import { useFullUserDetails } from '../../utils/hooks';
import { makeStyles, mergeClasses } from '@fluentui/react-components';
import { p4Tokens } from '../../styles/themes/default/p4FluentTheme';
import ViewContext from '../Context/ViewContext';
import { Analytics } from '../../mixpanel';
import { useP4ToastController } from '../../views/system/NotificationsArea';
import { AlertType } from '../../graphql/schema/p4types';

const SCROLL_STEP = 10;
const TAB_SIZE = 120;

export type TabPanelProps<TabId = string> = {
  ref?: any;
  active?: TabId; //Active tab id
  children?: any;
  sortable?: boolean;
  switchTabs?: boolean;
  validationFalsePositives?: string[];
  className?: string;
  onlyRenderWhenActive?: boolean;
  screenName?: string;
  customAction?: ReactElement;
  onActiveTabChange?: (activeTabId: Maybe<TabId>) => void;
  tabs?: { className: string }; //Tabs slot.
};

const unwrapChildren = (children: Array<ReactElement>): Array<ReactElement> => {
  return children.flatMap((child) => {
    if ((child as any).type === React.Fragment) {
      return unwrapChildren((child as any).props.children);
    }
    return [child];
  });
};

const DropdownLink = memo<{
  children: Array<any>;
  containerRef: MutableRefObject<any>;
  selectActiveTabId: Function;
  tabActionItems: Array<any>;
}>(({ children: tabChildren, containerRef, selectActiveTabId, tabActionItems }) => {
  const fullUserDetails = useFullUserDetails();

  let idx = -1;
  const children = React.Children.toArray(tabChildren) as Array<ReactElement>;
  const unwrappedChildren = unwrapChildren(children).filter((a: any) => a !== null);

  unwrappedChildren.sort((a, b) => a?.props?.label.localeCompare(b.props.label));

  const menuProps: { items: Array<IContextualMenuItem> } = {
    items: unwrappedChildren
      .filter((_tab) => {
        const permitted = _tab.props.permissionReference
          ? checkPermission({ app: { fullUserDetails } }, _tab.props.permissionReference)
          : true;
        return _tab.props.shouldRender !== false && permitted;
      })
      .map((_tab: any) => {
        const { id, label } = _tab.props;

        idx++;
        const scrollPos = idx * TAB_SIZE;
        return {
          key: `tabList${id}`,
          text: label,
          onClick: (e) => {
            e?.preventDefault();
            containerRef.current.scrollLeft = scrollPos;
            selectActiveTabId(id, true);
          },
          'data-testid': `tabList${id}`,
        };
      }),
  };

  menuProps.items.unshift({
    key: 'tabSelection',
    itemType: ContextualMenuItemType.Header,
    text: 'Tab Selection',
  });

  menuProps.items.unshift(...tabActionItems);

  return (
    <div className={styles.lastTabControl}>
      <IconButton
        menuProps={menuProps}
        iconProps={{ iconName: 'FolderHorizontal' }}
        title={'Tab selection'}
        ariaLabel={'Tab selection'}
      />
    </div>
  );
});

const useTabActionItems: <TabId extends unknown>(options: {
  tabs: Array<ReactElement<ITabProps<TabId>>>;
  screenName: Maybe<string>;
  tabFavouriteMap?: Map<number, boolean>;
}) => [Array<IContextualMenuItem>, ReactElement] = ({ tabs, screenName, tabFavouriteMap }) => {
  const [reorderModalOpen, setReorderModalOpen] = useState<boolean>(false);
  const warningsGenerated = useRef<{ [tabKey: string]: boolean }>({});

  const tabNames = useMemo(
    () =>
      tabs.map((tab) => {
        if (!tab.props.label && !warningsGenerated.current[(tab.props as any).key]) {
          warningsGenerated.current[(tab.props as any).key] = true;
          console.warn(
            'Invalid tab child could not be loaded for tab organization, conditionally rendered tabs should be replaced with "shouldRender" prop usage: ',
            tab
          );
        }
        return tab.props.label;
      }),
    [tabs]
  );

  const tabActionReorderModal = (
    <TabOrganizationModal
      tabNames={tabNames}
      modalOpen={reorderModalOpen}
      onClose={() => setReorderModalOpen(false)}
      screenName={screenName}
      tabFavouriteMap={tabFavouriteMap}
    />
  );

  const tabActionItems = useMemo(() => {
    return [
      {
        key: 'tabActions',
        itemType: ContextualMenuItemType.Header,
        text: 'Tab Actions',
      },
      {
        key: `tabActionReorder`,
        text: 'Organize Tabs',
        onClick: () => {
          setReorderModalOpen(true);
        },
        'data-testid': `organizeTabs${screenName}`,
        iconProps: { iconName: 'BuildQueue', style: { color: 'black' } },
      },
    ];
  }, [screenName]);

  return [tabActionItems, tabActionReorderModal];
};

const useStyles = makeStyles({
  tabs: {
    backgroundColor: p4Tokens.colorHeaderBackground,
  },
});

const TabPanel = <TabId extends unknown>(
  {
    active,
    children,
    customAction,
    screenName: overrideScreenName,
    sortable,
    switchTabs,
    validationFalsePositives,
    onActiveTabChange,
    tabs: tabsSlot,
  }: TabPanelProps<TabId>,
  ref: Ref<any | undefined>
) => {
  const connectedForm = useContext(ConnectedFormContext);

  const [activeTabId, setActiveTabId] = useState<Maybe<TabId>>(null);
  const [tabClicked, setTabClicked] = useState<boolean>(false);
  const [hideScrollLeft, setHideScrollLeft] = useState<boolean>(false);
  const [hideScrollRight, setHideScrollRight] = useState<boolean>(false);
  const { dispatchSimpleToast } = useP4ToastController();

  const holdLeftScroll = useRef(null as any);
  const holdRightScroll = useRef(null as any);

  const containerRef = useRef<any>();
  const contentRef = useRef<any>();

  const userContext = useContext(UserContext);

  const tabStyles = useStyles();

  const { fullScreenName } = useContext(ViewContext);
  const viewScreenName = overrideScreenName ?? fullScreenName;

  const tabFavouriteMap = useMemo(() => {
    const systemTabs = userContext.systemUserTabOrders?.list?.filter(
      (value) => value?.viewScreenName === viewScreenName
    );
    return new Map(systemTabs?.map((tab) => [tab?.tabPosition as number, tab?.tabFavourite]));
  }, [userContext.systemUserTabOrders, viewScreenName]);

  const [submitOption] = connectedForm.submitOptions ?? [];

  const sortedTabs: Array<ReactElement<ITabProps<TabId>>> = useMemo(() => {
    const newSortedTabs: Array<ReactElement<ITabProps<TabId>>> = [];

    (React.Children.toArray(children) as Array<ReactElement>).forEach((tab: ReactElement<ITabProps<TabId>>, index) => {
      let tabPosition =
        userContext.systemUserTabOrders?.list
          ?.filter((value) => value?.viewScreenName === viewScreenName)
          ?.find((value) => value?.tabName === tab?.props?.label)?.tabPosition ?? index;
      while (newSortedTabs[tabPosition]) {
        //Avoid overwriting any tabs if there's a positional conflict for any reason. (I.e. If we add a new tab & it falls back to 'index', makes sure it gets shifted to to a
        // unique location.
        tabPosition++;
      }

      newSortedTabs[tabPosition] = tab;
    });

    const noEmptyTabs = newSortedTabs.filter((value) => value);
    if (noEmptyTabs.length !== newSortedTabs.length) {
      console.warn(
        `Invalid children for tab panel detected on form ${connectedForm.name} - all tab panel children require a unique id.`
      );
    }

    return noEmptyTabs;
  }, [children, connectedForm.name, userContext.systemUserTabOrders?.list, viewScreenName]);

  const resetActiveTab = useCallback(
    (unexpectedTabId) => {
      // Use first tab as active if not have active indicator against them (first tab may have any sort index as this is
      // specified on the page, hence get first element in array rather than just sortedTabs[0])
      let activeTab: any;

      // if URL param specified use this rather than the one specified by in props
      const params = new URLSearchParams(window.location.search);
      if (params.has('activeTab')) {
        activeTab = sortedTabs.find((tabContent: any) => {
          return tabContent.props.id === params.get('activeTab');
        });
      }

      if (!activeTab) {
        activeTab = sortedTabs.find((tabContent: any, index) => {
          return tabFavouriteMap?.get(index) && tabContent?.props?.shouldRender !== false;
        });
      }

      if (!activeTab && active) {
        activeTab = sortedTabs.find((tabContent: any) => {
          return tabContent.props.id === active;
        });
      }

      if (!activeTab) {
        activeTab = sortedTabs.find((tabContent: any) => {
          return tabContent?.props?.shouldRender ?? true;
        });
      }

      if (!activeTab?.props?.id) {
        console.warn('Unable to find active tab', { sortedTabs, activeTab });
      }
      setActiveTabId(activeTab.props.id);

      if (unexpectedTabId !== undefined && unexpectedTabId !== null) {
        dispatchSimpleToast(
          'Tab Not Available',
          `The "${unexpectedTabId}" tab is unavailable. Redirecting to the "${activeTab?.props?.id}" tab.`,
          AlertType.Info
        );
        Analytics.track('Unexpected Tab Access (User selected a tab that should not have been available).', {
          unexpectedTabId,
        });
      }
    },
    [active, sortedTabs, tabFavouriteMap]
  );

  const shouldRenderFavourite = useMemo(() => {
    if (sortedTabs?.length && tabFavouriteMap?.size) {
      const indexOfFavourite =
        Array.from(tabFavouriteMap.entries()).find(([key, value]) => value === true)?.[0] ?? null;
      return !indexOfFavourite ? false : sortedTabs[indexOfFavourite]?.props?.shouldRender;
    }

    return false;
  }, [tabFavouriteMap, sortedTabs]);

  const prevShouldRenderFavouriteRef = useRef(shouldRenderFavourite);

  /**
   * Initialize active tab on first time load or if props.active changes ('active' dependency is on resetActiveTab).
   */
  useEffect(() => {
    const previousShouldRenderFavourite = prevShouldRenderFavouriteRef.current;
    if (sortedTabs?.length && (activeTabId === null || (!previousShouldRenderFavourite && shouldRenderFavourite))) {
      resetActiveTab?.();
    }

    prevShouldRenderFavouriteRef.current = shouldRenderFavourite;
  }, [sortedTabs, activeTabId, resetActiveTab, shouldRenderFavourite]);

  /**
   * If the uuid against the record changes and the submit option is 'NEW', reset the active tab to the default (Typically 'General' for a new record).
   */
  useEffect(() => {
    if (submitOption?.value === SubmitOption.New) {
      resetActiveTab();
    }
  }, [submitOption?.value, connectedForm.values?.uuid]);

  //Note - Prefer using 'props.active' to control the active tab, rather than setting the active tab directly with this.
  if (ref && 'current' in ref) {
    (ref as MutableRefObject<any>).current = {
      setActiveTabId: setActiveTabId,
    };
  }

  useEffect(() => {
    if (active && switchTabs && !tabClicked) {
      setActiveTabId(active);
    }
  }, [active, switchTabs, tabClicked]);

  const setScrollableRef = useRef<any>();
  setScrollableRef.current = () => {
    const scrollLeft = containerRef.current.scrollLeft;
    const newHideScrollLeft = scrollLeft === 0;

    const newHideScrollRight = contentRef.current.clientWidth < containerRef.current.clientWidth - 110;
    if (hideScrollLeft !== newHideScrollLeft || hideScrollRight !== newHideScrollRight) {
      setHideScrollLeft(newHideScrollLeft);
      setHideScrollRight(newHideScrollRight);
    }
  };

  const setScrollable = useCallback(() => setScrollableRef.current(), []);

  useEffect(() => {
    window.addEventListener('resize', setScrollable, false);
    return () => {
      window.removeEventListener('resize', setScrollable, false);
    };
  }, [setScrollable]);

  const visibleChildrenCount =
    children && Array.isArray(children)
      ? children.filter((child) => !!child && child?.props?.shouldRender !== false)?.length
      : 0;

  useEffect(() => {
    setScrollable();
  }, [setScrollable, visibleChildrenCount]);

  useEffect(() => {
    onActiveTabChange?.(activeTabId);
  }, [activeTabId, onActiveTabChange]);

  const selectActiveTabId = useCallback((activeTabId, clicked = false) => {
    setActiveTabId(activeTabId);
    setTabClicked(clicked);
  }, []);

  const scrollTabs = (step: number) => {
    if (containerRef.current?.scrollLeft == null) {
      return;
    }
    containerRef.current.scrollLeft += step;
    setScrollable();
  };

  const onLeftScrollButtonDown = () => {
    scrollToLeft();
    holdLeftScroll.current = setInterval(scrollToLeft, holdLeftScroll.current);
  };

  const onRightScrollButtonDown = () => {
    scrollToRight();
    holdRightScroll.current = setInterval(scrollToRight, holdRightScroll.current);
  };

  const cancelLeftScroll = () => {
    clearInterval(holdLeftScroll.current);
    containerRef.current.scrollLeft = Math.ceil(containerRef.current.scrollLeft / TAB_SIZE) * TAB_SIZE;
    holdLeftScroll.current = null;
  };

  const cancelRightScroll = () => {
    clearInterval(holdRightScroll.current);
    containerRef.current.scrollLeft = Math.ceil(containerRef.current.scrollLeft / TAB_SIZE) * TAB_SIZE;
    holdRightScroll.current = null;
  };

  const scrollToLeft = () => {
    scrollTabs(-SCROLL_STEP);
  };

  const scrollToRight = () => {
    scrollTabs(SCROLL_STEP);
  };

  const setLeftScrollLink = () => {
    if (hideScrollLeft && hideScrollRight) {
      cancelLeftScroll();
      return null;
    }
    const scrollStyle = { opacity: '1', cursor: 'pointer' };
    return (
      <a
        style={scrollStyle}
        className={styles.tabControl}
        onMouseDown={onLeftScrollButtonDown}
        onMouseUp={cancelLeftScroll}
      >
        <i className={`icon ${styles.scrollIcon}`}>keyboard_arrow_left</i>
      </a>
    );
  };

  const setToggleTabs = () => {
    const scrollStyle = { opacity: '1', cursor: 'pointer' };
    return (
      <div style={scrollStyle} className={styles.tabControl}>
        <i className={`icon ${styles.scrollIcon}`}>swap_horiz</i>
      </div>
    );
  };

  const setRightScrollLink = () => {
    if (hideScrollRight && hideScrollLeft) {
      cancelRightScroll();
      return null;
    }
    const scrollStyle = { opacity: '1', cursor: 'pointer' };
    return (
      <a
        style={scrollStyle}
        className={styles.tabControl}
        onMouseDown={onRightScrollButtonDown}
        onMouseUp={cancelRightScroll}
      >
        <i className={`icon ${styles.scrollIcon}`}>keyboard_arrow_right</i>
      </a>
    );
  };

  const [tabActionItems, tabActionItemModals] = useTabActionItems({
    tabs: sortedTabs,
    screenName: viewScreenName,
    tabFavouriteMap,
  });

  const checkValidationErrors = (tabId: string) => {
    if (!connectedForm) return false;
    const { validationErrors, registeredFields } = connectedForm;
    let hasErrors = false;
    if (validationErrors && Object.keys(validationErrors).length > 0) {
      const fields = Object.keys(validationErrors);
      fields.forEach((field) => {
        if (registeredFields?.[field] && registeredFields?.[field].tabId === tabId) {
          if (validationFalsePositives && validationFalsePositives.includes(field)) {
            return;
          }
          hasErrors = true;
        }
      });
    }
    return hasErrors;
  };

  const processTabs = (tabContent: ReactElement, idx: number) => {
    const tabProps = Object.assign({}, tabContent.props);
    delete tabProps.sort;

    let invalidClass = idx === 0 ? 'firstTab' : 'tab';
    const hasErrors = checkValidationErrors(tabProps.id);
    if (hasErrors) {
      invalidClass = 'tabInvalid';
    }
    const isActiveTab = activeTabId === tabProps.id;
    return (
      <Tab
        key={tabProps.id || idx}
        className={invalidClass}
        isActiveTab={isActiveTab}
        activateTab={selectActiveTabId}
        shouldRender={tabProps.shouldRender}
        permissionReference={tabProps.permissionReference}
        {...tabProps}
      />
    );
  };

  const processTabContents = (tabContent: ReactElement) => {
    const isActiveTab = activeTabId === tabContent.props.id;

    if (!tabContent.props.onlyRenderWhenActive || isActiveTab) {
      return (
        <TabContent
          id={tabContent.props.id}
          key={`tabContent${tabContent.props.id}`}
          isActiveTab={isActiveTab}
          activateTab={selectActiveTabId}
          shouldRender={tabContent.props.shouldRender}
          permissionReference={tabContent.props.permissionReference}
          tabContainer={tabContent.props.tabContainer}
          resetActiveTab={resetActiveTab}
        >
          {tabContent.props.children}
        </TabContent>
      );
    }
    return null;
  };

  const unwrappedChildren = unwrapChildren(sortedTabs);
  const tabs = unwrappedChildren.filter((a) => a !== null).map(processTabs);
  const contents = unwrappedChildren.map(processTabContents);
  const leftScroll = setLeftScrollLink();
  const rightScroll = setRightScrollLink();
  const toggleIcon = sortable ? setToggleTabs() : '';

  return (
    <>
      {tabActionItemModals}
      <div className={mergeClasses(styles.tabs, tabStyles.tabs, tabsSlot?.className)}>
        <div ref={containerRef} className={styles.tabsContainer}>
          <div ref={contentRef} className={styles.tabsContent}>
            {tabs}
          </div>
        </div>
        {toggleIcon}
        {leftScroll}
        {rightScroll}
        {customAction}
        <DropdownLink containerRef={containerRef} selectActiveTabId={selectActiveTabId} tabActionItems={tabActionItems}>
          {children}
        </DropdownLink>
      </div>
      {contents}
    </>
  );
};

export default forwardRef(TabPanel) as typeof TabPanel & RefAttributes<any>;
